import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import {
  ActivityIndicator,
  Image,
  ScrollView,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { Team } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';

interface SettingsTabProps {
  team: Team;
  isCaptain: boolean;
  isCurrentMember: boolean;
  editName: string;
  editDescription: string;
  editIsPublic: boolean;
  updating: boolean;
  uploadingLogo: boolean;
  onEditNameChange: (value: string) => void;
  onEditDescriptionChange: (value: string) => void;
  onEditIsPublicChange: (value: boolean) => void;
  onSave: () => void;
  onUploadLogo: () => void;
  onLeaveTeam: () => void;
  onDeleteTeam: () => void;
  colors: ThemeColors;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  team,
  isCaptain,
  isCurrentMember,
  editName,
  editDescription,
  editIsPublic,
  updating,
  uploadingLogo,
  onEditNameChange,
  onEditDescriptionChange,
  onEditIsPublicChange,
  onSave,
  onUploadLogo,
  onLeaveTeam,
  onDeleteTeam,
  colors,
}) => {
  const { t } = useTranslation();

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
      {isCaptain && (
        <>
          {/* Logo */}
          <View style={{ alignItems: 'center', marginBottom: 20 }}>
            <View style={{ width: 80, height: 80, borderRadius: 40, borderWidth: 2, borderColor: colors.border, backgroundColor: colors.surface, alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
              {team.logoUrl ? (
                <Image source={{ uri: team.logoUrl }} style={{ width: 80, height: 80, borderRadius: 40 }} />
              ) : (
                <Ionicons name="people" size={36} color={colors.textTertiary} />
              )}
            </View>
            <TouchableOpacity
              style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface }}
              onPress={onUploadLogo}
              disabled={uploadingLogo}
            >
              {uploadingLogo ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Text style={{ color: colors.primary, fontWeight: '500', fontSize: 14 }}>
                  {team.logoUrl ? t('teams.changeLogo') : t('teams.uploadLogo')}
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Name */}
          <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginBottom: 6 }}>{t('teams.teamName')}</Text>
          <TextInput
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, fontSize: 15, color: colors.text, marginBottom: 16 }}
            value={editName}
            onChangeText={onEditNameChange}
            maxLength={100}
          />

          {/* Description */}
          <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginBottom: 6 }}>{t('teams.description')}</Text>
          <TextInput
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, fontSize: 15, color: colors.text, marginBottom: 16, height: 80, textAlignVertical: 'top' }}
            value={editDescription}
            onChangeText={onEditDescriptionChange}
            multiline
            maxLength={500}
          />

          {/* Visibility */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <View style={{ flex: 1, marginRight: 12 }}>
              <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>{t('teams.public')}</Text>
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>{t('teams.publicDesc')}</Text>
            </View>
            <Switch
              value={editIsPublic}
              onValueChange={onEditIsPublicChange}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.primaryText}
            />
          </View>

          {/* Save button */}
          <TouchableOpacity
            style={{ paddingVertical: 14, borderRadius: 10, alignItems: 'center', backgroundColor: updating ? colors.border : colors.primary, marginBottom: 32 }}
            onPress={onSave}
            disabled={updating}
          >
            {updating ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={{ color: colors.primaryText, fontWeight: '700', fontSize: 16 }}>{t('teams.save')}</Text>
            )}
          </TouchableOpacity>
        </>
      )}

      {/* Leave team (non-captain members) */}
      {isCurrentMember && !isCaptain && (
        <TouchableOpacity
          style={{ paddingVertical: 14, borderRadius: 10, alignItems: 'center', borderWidth: 1, borderColor: colors.error, marginBottom: 12 }}
          onPress={onLeaveTeam}
        >
          <Text style={{ color: colors.error, fontWeight: '700', fontSize: 16 }}>{t('teams.leaveTeam')}</Text>
        </TouchableOpacity>
      )}

      {/* Delete team (captain only) */}
      {isCaptain && (
        <TouchableOpacity
          style={{ paddingVertical: 14, borderRadius: 10, alignItems: 'center', backgroundColor: colors.error }}
          onPress={onDeleteTeam}
        >
          <Text style={{ color: '#FFFFFF', fontWeight: '700', fontSize: 16 }}>{t('teams.deleteTeam')}</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  );
};
